import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminApi,
  type AdminEntry,
  type AdminPayout,
  type AdminTie,
  type AdminTournament,
  type PendingTiebreaker,
  type TournamentCreateInput,
} from '@/api/admin';
import { CasinoButton } from '@/components/CasinoButton';
import { LedNumber } from '@/components/LedNumber';
import { PhotoModal } from '@/components/PhotoModal';
import { SignPanel } from '@/components/SignPanel';
import { PayoutEditor } from '@/components/admin/PayoutEditor';
import { useAppConfig } from '@/hooks/useAppConfig';
import {
  computePotCents,
  formatDollars,
  validatePayoutStructure,
  type PayoutSlot,
} from '@/lib/payouts';
import { cn } from '@/lib/utils';
import { AdminHeader } from './TournamentNew';

const statusStyles: Record<string, string> = {
  setup: 'bg-cabinet-cream/10 text-cabinet-cream',
  active: 'bg-cabinet-gold text-cabinet-purple-ink',
  completed: 'bg-cabinet-flame-orange/30 text-cabinet-flame-orange',
  archived: 'bg-cabinet-cream/5 text-cabinet-cream/40',
};

export function TournamentManage() {
  const { id } = useParams<{ id: string }>();
  const tid = Number(id);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const detail = useQuery({
    queryKey: ['admin', 'tournament', tid],
    queryFn: () => adminApi.getTournament(tid),
    enabled: Number.isInteger(tid),
  });

  // Scroll to a hash anchor once the data is loaded (react-router doesn't do
  // this automatically on client-side navigation). Deep links like
  // /admin/tournament/4/manage#entries land in the right section.
  useEffect(() => {
    if (!location.hash || !detail.data) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) {
      requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  }, [location.hash, detail.data]);

  // Bust both the tournament detail and the global needs-attention summary
  // so the nav badge + /admin cards reflect verify/pay toggles immediately
  // instead of waiting for the 30s poll.
  const refetch = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['admin', 'tournament', tid] }),
      qc.invalidateQueries({ queryKey: ['admin', 'pending-summary'] }),
    ]);
  };

  const startM = useMutation({
    mutationFn: () => adminApi.startTournament(tid),
    onSuccess: refetch,
  });
  const closeM = useMutation({
    mutationFn: () => adminApi.closeTournament(tid),
    onSuccess: refetch,
  });
  const tbM = useMutation({
    mutationFn: (userIds: number[]) => adminApi.tiebreaker(tid, userIds),
    onSuccess: refetch,
  });
  const togglePaid = useMutation({
    mutationFn: (entryId: number) => adminApi.toggleEntryPaid(entryId),
    onSuccess: refetch,
  });
  const toggleVerified = useMutation({
    mutationFn: (entryId: number) => adminApi.toggleEntryVerified(entryId),
    onSuccess: refetch,
  });
  const togglePayout = useMutation({
    mutationFn: (payoutId: number) => adminApi.togglePayoutPaid(payoutId),
    onSuccess: refetch,
  });

  const [closeError, setCloseError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const deleteM = useMutation({
    mutationFn: () => adminApi.deleteTournament(tid),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin', 'tournaments'] });
      navigate('/admin');
    },
    onError: (err) => {
      const e = err as { error?: string; reason?: string };
      setDeleteError(
        e.reason === 'has_entries'
          ? 'Cannot delete — entries already exist.'
          : e.reason === 'not_in_setup'
            ? 'Cannot delete — tournament is no longer in setup.'
            : 'Could not delete.',
      );
    },
  });

  if (!Number.isInteger(tid)) {
    return (
      <main className="mx-auto max-w-2xl px-5 py-12">
        <p className="text-cabinet-flame-red">Invalid tournament id.</p>
      </main>
    );
  }
  if (detail.isLoading) {
    return (
      <main className="mx-auto max-w-2xl px-5 py-12">
        <p className="text-cabinet-cream/60">Loading…</p>
      </main>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <main className="mx-auto max-w-2xl px-5 py-12">
        <p className="text-cabinet-flame-red">Couldn't load tournament.</p>
      </main>
    );
  }

  const {
    tournament,
    entries,
    payouts,
    ties,
    potCents,
    pendingTiebreakers,
    entryCount,
  } = detail.data;
  const isSetup = tournament.status === 'setup';
  const isActive = tournament.status === 'active';
  const isCompleted = tournament.status === 'completed';

  const triggerTiebreaker = (tie: AdminTie) => {
    tbM.mutate(tie.userIds);
  };

  const pendingByRank = new Map<number, PendingTiebreaker>(
    pendingTiebreakers.map((p) => [p.rank, p]),
  );

  const close = async () => {
    setCloseError(null);
    try {
      await closeM.mutateAsync();
    } catch (err) {
      const e = err as { error?: string };
      if (e.error === 'unresolved_ties') {
        setCloseError('Resolve all paying-position ties before closing.');
      } else {
        setCloseError('Could not close tournament.');
      }
    }
  };

  return (
    <main className="mx-auto max-w-4xl px-5 py-8 space-y-6">
      <AdminHeader
        title={tournament.name}
        crumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'Tournaments', to: '/admin' },
          { label: tournament.name },
        ]}
      />

      <section className="cabinet-card p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-cabinet-cream/60">
              {tournament.machineName}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-4">
              <SignPanel size="md">
                <h2 className="text-marquee text-3xl uppercase leading-none">
                  {tournament.name}
                </h2>
              </SignPanel>
              <span
                className={cn(
                  'rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-widest font-semibold',
                  statusStyles[tournament.status],
                )}
              >
                {tournament.status}
              </span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/50">
              Pot
            </p>
            <LedNumber
              value={Math.round(potCents / 100)}
              currency
              digits={5}
              size="md"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <Stat label="Entry fee" value={formatDollars(tournament.entryFeeCents)} />
          <Stat label="Pulls" value={`${tournament.pullCount} × ${tournament.creditsPerPull}`} />
          <Stat label="Max/player" value={`${tournament.maxEntriesPerPlayer}`} />
          <Stat label="Tiebreaker pulls" value={`${tournament.tiebreakerPulls}`} />
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2">
          {isSetup && (
            <>
              <CasinoButton onClick={() => startM.mutate()} disabled={startM.isPending}>
                {startM.isPending ? 'Starting…' : 'Start tournament'}
              </CasinoButton>
              <CasinoButton
                variant="ghost"
                onClick={() => setEditing(true)}
                disabled={editing}
              >
                Edit details
              </CasinoButton>
              <button
                type="button"
                onClick={() => {
                  setDeleteError(null);
                  setConfirmDelete(true);
                }}
                disabled={entryCount > 0 || deleteM.isPending}
                title={
                  entryCount > 0
                    ? 'Cannot delete — entries exist'
                    : 'Delete this tournament'
                }
                className="text-sm text-cabinet-cream/40 hover:text-cabinet-flame-orange disabled:opacity-30 disabled:hover:text-cabinet-cream/40 px-3"
              >
                Delete
              </button>
            </>
          )}
          {isActive && (
            <>
              <CasinoButton variant="red" onClick={close} disabled={closeM.isPending}>
                {closeM.isPending ? 'Closing…' : 'Close tournament'}
              </CasinoButton>
              <span
                className="text-xs text-cabinet-cream/40"
                title="Tournament locked once started"
              >
                Editing locked
              </span>
            </>
          )}
          {isCompleted && (
            <span className="text-sm text-cabinet-cream/60">
              Closed {formatTs(tournament.completedAt)}.
            </span>
          )}
        </div>
        {closeError && (
          <p className="text-sm text-cabinet-flame-orange">{closeError}</p>
        )}
        {deleteError && (
          <p className="text-sm text-cabinet-flame-orange">{deleteError}</p>
        )}
      </section>

      {editing && isSetup && (
        <EditPanel
          tournament={tournament}
          onClose={() => setEditing(false)}
          onSaved={refetch}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Delete this tournament?"
          body={`"${tournament.name}" will be permanently removed. This can't be undone.`}
          confirmLabel={deleteM.isPending ? 'Deleting…' : 'Delete tournament'}
          confirmVariant="red"
          onConfirm={() => deleteM.mutate()}
          onCancel={() => setConfirmDelete(false)}
        />
      )}

      {!isCompleted && ties.length > 0 && (
        <section className="cabinet-card p-5 space-y-3 border-cabinet-flame-orange/50">
          <h3 className="font-display text-cabinet-flame-orange text-lg uppercase tracking-widest">
            Unresolved ties
          </h3>
          <ul className="space-y-2">
            {ties.map((tie) => {
              const pending = pendingByRank.get(tie.rank);
              const round = tieRound(entries, tie.userIds);
              return (
                <li
                  key={tie.rank}
                  className="flex items-center justify-between gap-3 rounded-md bg-cabinet-flame-red/10 ring-1 ring-cabinet-flame-orange px-3 py-2"
                >
                  <span className="text-sm text-cabinet-cream">
                    Tie for{' '}
                    <strong className="text-cabinet-flame-orange">
                      {placeLabel(tie.rank)}
                    </strong>{' '}
                    — {tie.userIds.length} players (
                    {tie.userIds
                      .map((uid) => userName(entries, uid))
                      .join(', ')}
                    )
                  </span>
                  {pending ? (
                    <span className="rounded-full bg-cabinet-flame-orange/20 ring-1 ring-cabinet-flame-orange px-3 py-1.5 text-[10px] uppercase tracking-widest font-display text-cabinet-flame-orange">
                      {pending.state === 'awaiting_verification'
                        ? 'Tiebreaker pending — awaiting verification'
                        : 'Tiebreaker pending — awaiting scores'}
                    </span>
                  ) : (
                    <CasinoButton
                      variant="red"
                      onClick={() => triggerTiebreaker(tie)}
                      disabled={tbM.isPending}
                    >
                      {round > 0
                        ? `Trigger tiebreaker (round ${round + 1})`
                        : 'Trigger tiebreaker'}
                    </CasinoButton>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <EntryTable
        entries={entries}
        onTogglePaid={(id) => togglePaid.mutate(id)}
        onToggleVerified={(id) => toggleVerified.mutate(id)}
        onRefetch={refetch}
      />

      {payouts.length > 0 && (
        <PayoutTable
          payouts={payouts}
          onTogglePaid={(id) => togglePayout.mutate(id)}
        />
      )}

    </main>
  );
}

function EntryTable({
  entries,
  onTogglePaid,
  onToggleVerified,
  onRefetch,
}: {
  entries: AdminEntry[];
  onTogglePaid: (id: number) => void;
  onToggleVerified: (id: number) => void;
  onRefetch: () => void;
}) {
  const [editing, setEditing] = useState<number | null>(null);
  const [photoModalEntry, setPhotoModalEntry] = useState<AdminEntry | null>(null);

  // Per-user entry counts — only show "Entry #N" for players with >1 entry.
  const entriesPerUser = new Map<number, number>();
  for (const e of entries) {
    if (e.isTiebreaker) continue;
    entriesPerUser.set(e.userId, (entriesPerUser.get(e.userId) ?? 0) + 1);
  }

  return (
    <section id="entries" className="cabinet-card p-5 space-y-3 scroll-mt-20">
      <h3 className="font-display text-cabinet-gold text-lg uppercase tracking-widest">
        Entries ({entries.length})
      </h3>
      {entries.length === 0 && (
        <p className="text-sm text-cabinet-cream/60">No entries yet.</p>
      )}
      <ul className="space-y-2">
        {entries.map((e) => {
          const userTotalEntries = entriesPerUser.get(e.userId) ?? 1;
          const showEntryNumber = !e.isTiebreaker && userTotalEntries > 1;
          return (
          <li
            key={e.id}
            className={cn(
              'rounded-md ring-1 px-3 py-2.5',
              e.isTiebreaker
                ? 'ring-cabinet-flame-orange/40 bg-cabinet-flame-red/5'
                : 'ring-cabinet-cream/10 bg-cabinet-purple-ink/40',
            )}
          >
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-cabinet-cream truncate">
                  {e.userDisplayName || e.userEmail || `User ${e.userId}`}
                  {showEntryNumber && (
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-cabinet-cream/40">
                      Entry #{e.entryNumber}
                    </span>
                  )}
                  {e.isTiebreaker && (
                    <span className="ml-2 text-xs uppercase tracking-widest text-cabinet-flame-orange">
                      Tiebreaker
                    </span>
                  )}
                  {e.editCount > 0 && (
                    <span className="ml-2 text-[10px] uppercase text-cabinet-cream/50">
                      ✎ {e.editCount} edit{e.editCount === 1 ? '' : 's'}
                    </span>
                  )}
                </p>
                <p className="text-xs text-cabinet-cream/50 tabular-nums">
                  {e.startingBalance !== null && e.endingBalance !== null
                    ? `${e.startingBalance} → ${e.endingBalance}`
                    : 'pending submission'}
                  {e.verifiedByAdmin
                    ? ' · verified'
                    : e.submittedAt
                    ? ' · awaiting verify'
                    : ''}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/50">
                  Score
                </p>
                <p className="font-led tabular-nums text-cabinet-led-on text-xl">
                  {e.score ?? '—'}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                {(e.hasStartingPhoto || e.hasEndingPhoto) && (
                  <button
                    type="button"
                    onClick={() => setPhotoModalEntry(e)}
                    className="text-xs text-cabinet-gold hover:text-cabinet-gold-bright"
                  >
                    View photos
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setEditing(editing === e.id ? null : e.id)}
                  className="text-xs text-cabinet-gold hover:text-cabinet-gold-bright"
                >
                  {editing === e.id ? 'Cancel' : 'Edit'}
                </button>
                <button
                  type="button"
                  onClick={() => onToggleVerified(e.id)}
                  className={cn(
                    'text-xs',
                    e.verifiedByAdmin
                      ? 'text-cabinet-gold-bright hover:text-cabinet-gold'
                      : 'text-cabinet-cream/50 hover:text-cabinet-cream',
                  )}
                >
                  {e.verifiedByAdmin ? 'Verified ✓' : 'Verify'}
                </button>
                <button
                  type="button"
                  onClick={() => onTogglePaid(e.id)}
                  className={cn(
                    'text-xs',
                    e.paid
                      ? 'text-cabinet-gold-bright hover:text-cabinet-gold'
                      : 'text-cabinet-cream/50 hover:text-cabinet-cream',
                  )}
                >
                  {e.paid ? 'Paid ✓' : 'Mark paid'}
                </button>
              </div>
            </div>

            {editing === e.id && (
              <EntryEditForm
                entry={e}
                onDone={() => {
                  setEditing(null);
                  onRefetch();
                }}
                onCancel={() => setEditing(null)}
              />
            )}
          </li>
          );
        })}
      </ul>
      {photoModalEntry && (
        <PhotoModal
          entryId={photoModalEntry.id}
          title={photoModalEntry.userDisplayName ?? photoModalEntry.userEmail ?? `User ${photoModalEntry.userId}`}
          startingBalance={photoModalEntry.startingBalance}
          endingBalance={photoModalEntry.endingBalance}
          hasStartingPhoto={photoModalEntry.hasStartingPhoto}
          hasEndingPhoto={photoModalEntry.hasEndingPhoto}
          onClose={() => setPhotoModalEntry(null)}
        />
      )}
    </section>
  );
}

function EntryEditForm({
  entry,
  onDone,
  onCancel,
}: {
  entry: AdminEntry;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [starting, setStarting] = useState(entry.startingBalance ?? 0);
  const [ending, setEnding] = useState(entry.endingBalance ?? 0);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      adminApi.editEntry(entry.id, {
        startingBalance: starting,
        endingBalance: ending,
        reason: reason.trim(),
      }),
    onSuccess: () => onDone(),
    onError: () => setErr('Could not save edit.'),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (reason.trim().length < 3) {
          setErr('Reason is required (min 3 chars).');
          return;
        }
        save.mutate();
      }}
      className="mt-3 pt-3 border-t border-cabinet-cream/10 space-y-2"
    >
      <div className="grid grid-cols-2 gap-2">
        <label className="block text-xs">
          <span className="text-cabinet-cream/60 uppercase tracking-widest">
            Starting
          </span>
          <input
            type="number"
            min={0}
            value={starting}
            onChange={(e) => setStarting(parseInt(e.target.value) || 0)}
            className="mt-1 w-full bg-black/50 border border-cabinet-cream/20 rounded-md px-2 py-1 tabular-nums focus:border-cabinet-gold focus:outline-none"
          />
        </label>
        <label className="block text-xs">
          <span className="text-cabinet-cream/60 uppercase tracking-widest">
            Ending
          </span>
          <input
            type="number"
            min={0}
            value={ending}
            onChange={(e) => setEnding(parseInt(e.target.value) || 0)}
            className="mt-1 w-full bg-black/50 border border-cabinet-cream/20 rounded-md px-2 py-1 tabular-nums focus:border-cabinet-gold focus:outline-none"
          />
        </label>
      </div>
      <label className="block text-xs">
        <span className="text-cabinet-cream/60 uppercase tracking-widest">
          Reason
        </span>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
          minLength={3}
          maxLength={500}
          placeholder="photo recount, mistyped, …"
          className="mt-1 w-full bg-black/50 border border-cabinet-cream/20 rounded-md px-2 py-1 focus:border-cabinet-gold focus:outline-none"
        />
      </label>
      <p className="text-[10px] text-cabinet-cream/40 tabular-nums">
        New score: {ending - starting}
      </p>
      <div className="flex items-center gap-2">
        <CasinoButton type="submit" variant="gold" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </CasinoButton>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-cabinet-cream/60 hover:text-cabinet-cream"
        >
          Cancel
        </button>
      </div>
      {err && <p className="text-xs text-cabinet-flame-orange">{err}</p>}
    </form>
  );
}

function PayoutTable({
  payouts,
  onTogglePaid,
}: {
  payouts: AdminPayout[];
  onTogglePaid: (id: number) => void;
}) {
  return (
    <section className="cabinet-card p-5 space-y-3">
      <h3 className="font-display text-cabinet-gold text-lg uppercase tracking-widest">
        Payouts
      </h3>
      <ul className="space-y-2">
        {payouts.map((p) => (
          <li
            key={p.id}
            className="flex items-center justify-between rounded-md ring-1 ring-cabinet-cream/10 bg-cabinet-purple-ink/40 px-3 py-2"
          >
            <div className="flex items-center gap-3">
              <span className="font-display text-cabinet-gold-bright text-xl w-10 text-center">
                {placeLabel(p.place)}
              </span>
              <span className="text-cabinet-cream">
                {p.userDisplayName || `User ${p.userId}`}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-led tabular-nums text-cabinet-led-on">
                {formatDollars(p.amountCents)}
              </span>
              <button
                type="button"
                onClick={() => onTogglePaid(p.id)}
                className={cn(
                  'text-xs uppercase tracking-widest',
                  p.paid
                    ? 'text-cabinet-gold-bright hover:text-cabinet-gold'
                    : 'text-cabinet-cream/50 hover:text-cabinet-cream',
                )}
              >
                {p.paid ? 'Paid ✓' : 'Mark paid'}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-cabinet-purple-ink/40 ring-1 ring-cabinet-cream/10 px-3 py-2">
      <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/50">
        {label}
      </p>
      <p className="text-sm text-cabinet-cream tabular-nums">{value}</p>
    </div>
  );
}

function placeLabel(p: number): string {
  const v = p % 100;
  if (v >= 11 && v <= 13) return `${p}th`;
  switch (p % 10) {
    case 1:
      return `${p}st`;
    case 2:
      return `${p}nd`;
    case 3:
      return `${p}rd`;
    default:
      return `${p}th`;
  }
}

function userName(entries: AdminEntry[], userId: number): string {
  const e = entries.find((x) => x.userId === userId);
  return e?.userDisplayName || e?.userEmail || `User ${userId}`;
}

function formatTs(s: string | null): string {
  if (!s) return '';
  return new Date(s).toLocaleString();
}

/**
 * How many tiebreaker rounds have already happened for this tied group of
 * users? = the max number of TB entries any one of the users has.
 */
function tieRound(entries: AdminEntry[], userIds: number[]): number {
  let max = 0;
  for (const uid of userIds) {
    const n = entries.filter((e) => e.userId === uid && e.isTiebreaker).length;
    if (n > max) max = n;
  }
  return max;
}

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  confirmVariant = 'red',
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  confirmVariant?: 'red' | 'gold';
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-5"
    >
      <div className="cabinet-card max-w-sm w-full p-5 space-y-3 border-cabinet-flame-orange/60">
        <h3 className="font-display text-cabinet-flame-orange text-lg uppercase tracking-widest">
          {title}
        </h3>
        <p className="text-sm text-cabinet-cream/80">{body}</p>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm text-cabinet-cream/60 hover:text-cabinet-cream px-3"
          >
            Cancel
          </button>
          <CasinoButton variant={confirmVariant} onClick={onConfirm}>
            {confirmLabel}
          </CasinoButton>
        </div>
      </div>
    </div>
  );
}

function EditPanel({
  tournament,
  onClose,
  onSaved,
}: {
  tournament: AdminTournament;
  onClose: () => void;
  onSaved: () => void;
}) {
  const config = useAppConfig();
  const [name, setName] = useState(tournament.name);
  const [machineName, setMachineName] = useState(tournament.machineName);
  // Numeric fields are kept as strings so the user can clear and retype them
  // without the controlled input snapping back to a fallback value mid-keystroke.
  const [entryFee, setEntryFee] = useState(String(tournament.entryFeeCents / 100));
  const [maxEntries, setMaxEntries] = useState(String(tournament.maxEntriesPerPlayer));
  const [pullCount, setPullCount] = useState(String(tournament.pullCount));
  const [creditsPerPull, setCreditsPerPull] = useState(String(tournament.creditsPerPull));
  const [tiebreakerPulls, setTiebreakerPulls] = useState(String(tournament.tiebreakerPulls));
  const [expectedEntries, setExpectedEntries] = useState('8');
  const [slots, setSlots] = useState<PayoutSlot[]>(tournament.payoutStructure);
  const [error, setError] = useState<string | null>(null);

  const entryFeeCents = Math.max(0, Math.round((parseFloat(entryFee) || 0) * 100));
  const expectedEntriesNum = Math.max(1, parseInt(expectedEntries, 10) || 1);
  const potCents = computePotCents(entryFeeCents, expectedEntriesNum);
  const payoutValid = validatePayoutStructure(slots);

  const save = useMutation({
    mutationFn: (input: Partial<TournamentCreateInput>) =>
      adminApi.updateTournament(tournament.id, input),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err) => {
      const e = err as { error?: string; message?: string };
      setError(e.message ?? e.error ?? 'Could not save.');
    },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!payoutValid.ok) {
      setError(payoutValid.error ?? 'invalid payout');
      return;
    }
    save.mutate({
      name: name.trim(),
      machineName: machineName.trim() || config.machineName,
      entryFeeCents,
      maxEntriesPerPlayer: Math.max(1, parseInt(maxEntries, 10) || 1),
      pullCount: Math.max(1, parseInt(pullCount, 10) || 1),
      creditsPerPull: Math.max(1, parseInt(creditsPerPull, 10) || 1),
      tiebreakerPulls: Math.max(1, parseInt(tiebreakerPulls, 10) || 1),
      payoutStructure: slots,
    });
  }

  const inputCls =
    'w-full rounded-md bg-black/50 border-2 border-cabinet-cream/20 px-3 py-2.5 text-cabinet-cream focus:border-cabinet-gold focus:outline-none focus:ring-2 focus:ring-cabinet-gold/40 transition tabular-nums';

  return (
    <form onSubmit={submit} className="cabinet-card p-5 space-y-4">
      <h3 className="font-display text-cabinet-gold text-lg uppercase tracking-widest">
        Edit details
      </h3>
      <label className="block">
        <span className="block text-xs uppercase tracking-widest text-cabinet-cream/60 mb-1">
          Name
        </span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className={inputCls}
        />
      </label>
      <label className="block">
        <span className="block text-xs uppercase tracking-widest text-cabinet-cream/60 mb-1">
          Machine name
        </span>
        <input
          value={machineName}
          onChange={(e) => setMachineName(e.target.value)}
          className={inputCls}
        />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-xs uppercase tracking-widest text-cabinet-cream/60 mb-1">
            Entry fee ($)
          </span>
          <input
            type="number"
            step="0.01"
            min={0}
            value={entryFee}
            onChange={(e) => setEntryFee(e.target.value)}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-widest text-cabinet-cream/60 mb-1">
            Max entries / player
          </span>
          <input
            type="number"
            min={1}
            max={10}
            value={maxEntries}
            onChange={(e) => setMaxEntries(e.target.value)}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-widest text-cabinet-cream/60 mb-1">
            Pulls
          </span>
          <input
            type="number"
            min={1}
            value={pullCount}
            onChange={(e) => setPullCount(e.target.value)}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-widest text-cabinet-cream/60 mb-1">
            Credits / pull
          </span>
          <input
            type="number"
            min={1}
            value={creditsPerPull}
            onChange={(e) => setCreditsPerPull(e.target.value)}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-widest text-cabinet-cream/60 mb-1">
            Tiebreaker pulls
          </span>
          <input
            type="number"
            min={1}
            value={tiebreakerPulls}
            onChange={(e) => setTiebreakerPulls(e.target.value)}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-widest text-cabinet-cream/60 mb-1">
            Preview entries
          </span>
          <input
            type="number"
            min={1}
            value={expectedEntries}
            onChange={(e) => setExpectedEntries(e.target.value)}
            className={inputCls}
          />
        </label>
      </div>

      <div className="rounded-md bg-black/40 ring-1 ring-cabinet-gold/20 px-4 py-2 flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-cabinet-cream/60">
          Preview pot
        </span>
        <span className="font-display text-cabinet-gold-bright text-xl tabular-nums">
          {formatDollars(potCents)}
        </span>
      </div>

      <PayoutEditor potCents={potCents} slots={slots} onChange={setSlots} />

      {error && (
        <div className="rounded-md bg-cabinet-flame-red/20 border border-cabinet-flame-red px-3 py-2 text-sm text-cabinet-cream">
          {error}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <CasinoButton type="submit" disabled={save.isPending || !payoutValid.ok}>
          {save.isPending ? 'Saving…' : 'Save changes'}
        </CasinoButton>
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-cabinet-cream/60 hover:text-cabinet-cream px-3"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

